import React, { useEffect, useState } from 'react';
import FlyerViewer from '../../components/FlyerViewer';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL, formatDate } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import ShareButton from '../../components/ShareButton';
import { useAuth } from '../../context/AuthContext';
import {
  MessageCircleQuestion, ShieldAlert, Scale, HeartPulse, ArrowRight,
  FileBadge, Briefcase, Users, GraduationCap, FileText, Building2, School,
  CheckCircle2, Search, Megaphone, Star,
} from 'lucide-react';

const CAT_ICONS = {
  licensing: FileBadge, careers: Briefcase, supervision: Users, internships: GraduationCap,
  documentation: FileText, 'private-practice': Building2, 'medical-social-work': HeartPulse,
  'school-social-work': School,
};

const BENEFITS = [
  'Get answers from experienced social workers',
  'Share your knowledge and lift others up',
  'Stay informed on best practices and resources',
  'Build meaningful professional connections',
];

const AskSocialWorker = () => {
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [featured, setFeatured] = useState(null);

  useEffect(() => {
    document.title = 'Ask a Social Worker | Community Questions | I Need A Social Worker';
    let m = document.querySelector('meta[name="description"]');
    if (!m) { m = document.createElement('meta'); m.name = 'description'; document.head.appendChild(m); }
    m.content = 'A supportive, moderated community for social workers at every stage of their career. Ask questions, share knowledge, and grow together. Ask. Learn. Grow. Together.';
  }, []);

  useEffect(() => {
    axios.get(`${API_URL}/community/categories`).then((r) => setCategories(r.data.categories || [])).catch(() => {});
    axios.get(`${API_URL}/community/announcements`).then((r) => setAnnouncements(r.data.announcements || [])).catch(() => {});
    axios.get(`${API_URL}/community/featured`).then((r) => setFeatured(r.data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-[#F7F5EF]">
      {/* Hero */}
      <section className="relative bg-primary pt-24 pb-14 overflow-hidden" data-testid="ask-sw-hero">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl text-white">
            <p className="inline-flex items-center gap-2 text-secondary font-semibold tracking-widest uppercase text-xs mb-3">
              <MessageCircleQuestion className="w-4 h-4" /> Community Questions
            </p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight">
              Ask a <span className="text-secondary italic">Social Worker</span><sup className="text-secondary text-[0.4em] align-super font-semibold ml-0.5">™</sup>
            </h1>
            <p className="mt-4 text-white/90 text-lg">Real questions. Professional support. Stronger social work.</p>
            <p className="mt-2 text-white/80">Connect with a community of social workers who share knowledge, experience, and encouragement.</p>
            <p className="mt-3 text-secondary font-semibold text-lg">Ask. Learn. Grow. Together.</p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Button onClick={() => navigate('/community/ask')} className="bg-secondary hover:bg-secondary-hover text-white h-11 px-6" data-testid="hero-ask-btn">
                Ask a Question <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button onClick={() => navigate('/community/questions')} variant="outline" className="border-white text-white hover:bg-white hover:text-primary h-11 px-6" data-testid="hero-browse-btn">
                <Search className="w-4 h-4 mr-2" /> Browse Questions
              </Button>
              {isAuthenticated && (
                <Button onClick={() => navigate('/community/my-activity')} variant="ghost" className="text-white hover:bg-white/10 h-11 px-6" data-testid="hero-my-activity-btn">
                  My Community Activity
                </Button>
              )}
              {user?.user_type === 'admin' && (
                <Button onClick={() => navigate('/admin/community')} variant="outline" className="border-secondary text-secondary hover:bg-secondary hover:text-white h-11 px-6" data-testid="hero-moderate-btn">
                  Moderate Community
                </Button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* INASW Ask a Social Worker flyer */}
      <section className="max-w-md mx-auto px-4 sm:px-6 lg:px-8 pt-8">
        <FlyerViewer src="/flyer-ask-social-worker.jpg" alt="INASW Ask a Social Worker" />
      </section>

      {/* Featured question */}
      {featured?.question && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8" data-testid="featured-question">
          <div className="bg-white rounded-2xl border border-secondary/40 shadow-sm overflow-hidden">
            <div className="bg-secondary/10 px-6 py-2.5 flex items-center justify-between flex-wrap gap-2">
              <span className="inline-flex items-center gap-2 text-secondary font-semibold uppercase text-xs tracking-widest"><Star className="w-4 h-4" /> Featured Question</span>
              <span className="text-xs text-gray-500">{featured.question.author_display}{featured.question.state ? ` · ${featured.question.state}` : ''}{featured.question.published_at ? ` · ${formatDate(featured.question.published_at)}` : ''}</span>
            </div>
            <div className="p-6">
              <p className="text-xs font-semibold text-secondary uppercase tracking-wide mb-1">Question from a social worker</p>
              <Link to={`/community/questions/${featured.question.question_id}`} className="font-serif text-xl sm:text-2xl font-bold text-primary hover:underline leading-tight" data-testid="featured-question-title">
                {featured.question.title}
              </Link>
              <p className="text-gray-700 mt-2">{featured.question.body}</p>
              {featured.top_answer && (
                <div className="mt-4 border-l-4 border-primary/30 pl-4">
                  <p className="text-xs font-semibold text-primary uppercase tracking-wide mb-1">Professional response</p>
                  <p className="text-gray-600 line-clamp-4">{featured.top_answer.body}</p>
                </div>
              )}
              <p className="mt-4 text-primary font-medium">What would you do in this situation? Share your professional perspective.</p>
              <div className="mt-4 flex flex-wrap gap-3">
                <Button onClick={() => navigate(`/community/questions/${featured.question.question_id}`)} className="bg-secondary hover:bg-secondary-hover text-white" data-testid="featured-respond-btn">
                  Read &amp; Respond <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <ShareButton path={`/community/questions/${featured.question.question_id}`} title={featured.question.title}
                  variant="outline" label="Share" testid="featured-share-btn" />
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Announcements */}
      {announcements.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8" data-testid="community-announcements">
          <div className="space-y-3">
            {announcements.map((a) => (
              <div key={a.announcement_id} className="bg-secondary/10 border border-secondary/40 rounded-xl p-4 flex gap-3" data-testid={`announcement-${a.announcement_id}`}>
                <Megaphone className="w-5 h-5 text-secondary flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-primary">{a.title}</p>
                  <p className="text-sm text-gray-700 mt-0.5 whitespace-pre-wrap">{a.body}</p>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Categories */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <h2 className="font-serif text-2xl font-bold text-primary mb-1">Browse Questions or Post Your Own</h2>
        <p className="text-gray-500 mb-5 text-sm">Choose a topic to explore community discussions.</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {categories.map((c) => {
            const Icon = CAT_ICONS[c.slug] || MessageCircleQuestion;
            return (
              <Link key={c.slug} to={`/community/questions?category=${c.slug}`} data-testid={`community-category-${c.slug}`}
                className="flex items-center gap-3 p-4 rounded-xl border bg-white text-gray-700 border-gray-200 hover:border-secondary hover:shadow-sm transition-all">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Icon className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="text-sm font-semibold leading-tight">{c.label}</p>
                  <p className="text-xs text-gray-400">{c.count} question{c.count === 1 ? '' : 's'}</p>
                </div>
              </Link>
            );
          })}
        </div>

        {/* Benefits */}
        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {BENEFITS.map((b) => (
            <div key={b} className="flex items-start gap-2 bg-white rounded-xl border p-4">
              <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
              <p className="text-sm text-gray-700">{b}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Community guidelines */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-10">
        <div className="bg-primary rounded-2xl p-6 sm:p-8 text-white">
          <h2 className="font-serif text-2xl font-bold text-center mb-6">Our Community Guidelines</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="flex gap-3">
              <ShieldAlert className="w-8 h-8 text-secondary flex-shrink-0" />
              <div>
                <h3 className="font-semibold">Protect Privacy</h3>
                <p className="text-sm text-white/80 mt-1">Do not share client-identifying information. Change any details that could identify a person.</p>
              </div>
            </div>
            <div className="flex gap-3">
              <Scale className="w-8 h-8 text-secondary flex-shrink-0" />
              <div>
                <h3 className="font-semibold">No Legal Advice</h3>
                <p className="text-sm text-white/80 mt-1">Do not request or provide legal advice. This community is not a substitute for an attorney.</p>
              </div>
            </div>
            <div className="flex gap-3">
              <HeartPulse className="w-8 h-8 text-secondary flex-shrink-0" />
              <div>
                <h3 className="font-semibold">Not for Emergencies</h3>
                <p className="text-sm text-white/80 mt-1">Do not request emergency clinical consultation. If you or someone you know is in crisis, call or text <strong>988</strong> or your local emergency services.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Flyer */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="flex justify-center" data-testid="ask-sw-flyer">
          <img src="/ask-social-worker-flyer.png" alt="Ask a Social Worker — a supportive community for social workers at every stage of their career"
            className="w-full max-w-2xl rounded-2xl shadow-lg border border-primary/10" loading="lazy" />
        </div>
      </section>
    </div>
  );
};

export default AskSocialWorker;
